import { ITaskUpdatePayload } from "@/src/api/tasksPageRequests";
import { ITasksTableRow } from "@/src/types/tasksTable";
import { TaskCreateFormState } from "./buildTaskCreatePayload";

export type TaskUpdateComparableState = {
  applicant_id?: number;
  assignee_id?: number;
  executor_id?: number;
  whoose: string;
  type: string;
  intake: string;
  admission_semester: string;
  form_of_education: string;
  selected_course: string;
  category: string;
  medium_of_instruction: string;
  duration: string;
  transfer_fee: string;
  priority: string;
  note: string;
  deadline_date: string;
  status: string;
  attached_file: string;
  tuition_fee: string;
  medical_insurance: string;
  residence_permit: string;
  other_expenses: string;
  acommodation: string;
  admission_fee: string;
  complete_one_year: string;
};

type TaskUpdateComparableKey = keyof TaskUpdateComparableState;

const STRING_KEYS: ReadonlyArray<Exclude<TaskUpdateComparableKey, "applicant_id" | "assignee_id" | "executor_id">> = [
  "whoose",
  "type",
  "intake",
  "admission_semester",
  "form_of_education",
  "selected_course",
  "category",
  "medium_of_instruction",
  "duration",
  "transfer_fee",
  "priority",
  "note",
  "deadline_date",
  "status",
  "attached_file",
  "tuition_fee",
  "medical_insurance",
  "residence_permit",
  "other_expenses",
  "acommodation",
  "admission_fee",
  "complete_one_year",
];

const NUMERIC_KEYS: ReadonlyArray<"applicant_id" | "assignee_id" | "executor_id"> = [
  "applicant_id",
  "assignee_id",
  "executor_id",
];

const normalizeString = (value: unknown): string => {
  if (value == null) return "";
  return String(value).trim();
};

const readAccountingField = (accounting: unknown, ...keys: string[]): string => {
  if (accounting == null || typeof accounting !== "object") return "";
  const record = accounting as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
};

export const taskRowToUpdateComparableState = (row: ITasksTableRow): TaskUpdateComparableState => ({
  applicant_id: row.student_id,
  assignee_id: row.assignee_id,
  executor_id: row.executor_id,
  whoose: normalizeString(row.whoose),
  type: normalizeString(row.type),
  intake: normalizeString(row.intake),
  admission_semester: normalizeString(row.admission_semester),
  form_of_education: normalizeString(row.form_of_education),
  selected_course: normalizeString(row.selected_course || row.course),
  category: normalizeString(row.category),
  medium_of_instruction: normalizeString(row.medium_of_instruction),
  duration: normalizeString(row.duration),
  transfer_fee: normalizeString(row.transfer_fee),
  priority: normalizeString(row.priority),
  note: normalizeString(row.note),
  deadline_date: normalizeString(row.deadline_date),
  status: normalizeString(row.status),
  attached_file: normalizeString(row.attached_file),
  tuition_fee: normalizeString(row.tuition_fee),
  medical_insurance: normalizeString(row.medical_insurance),
  residence_permit: normalizeString(row.residence_permit),
  other_expenses: normalizeString(row.other_expenses),
  acommodation: normalizeString(row.acommodation),
  admission_fee: normalizeString(row.admission_fee),
  complete_one_year: normalizeString(row.complete_one_year),
});

export const buildTaskUpdateComparableStateFromForm = (
  form: TaskCreateFormState,
  options?: {
    status?: string;
    attachedFile?: string | null;
    applicantId?: number;
    assigneeId?: number;
    executorId?: number;
    accounting?: unknown;
  },
): TaskUpdateComparableState => ({
  applicant_id: options?.applicantId,
  assignee_id: options?.assigneeId,
  executor_id: options?.executorId,
  whoose: normalizeString(form.whoose),
  type: normalizeString(form.type),
  intake: normalizeString(form.intake),
  admission_semester: normalizeString(form.admission_semester),
  form_of_education: normalizeString(form.form_of_education),
  selected_course: normalizeString(form.selected_course),
  category: normalizeString(form.category),
  medium_of_instruction: normalizeString(form.medium_of_instruction),
  duration: normalizeString(form.duration),
  transfer_fee: normalizeString(form.transfer_fee),
  priority: normalizeString(form.priority),
  note: normalizeString(form.note),
  deadline_date: normalizeString(form.deadline_date),
  status: normalizeString(options?.status),
  attached_file: normalizeString(options?.attachedFile),
  tuition_fee: readAccountingField(options?.accounting, "tuitionFee", "tuition_fee"),
  medical_insurance: readAccountingField(options?.accounting, "insurance", "medical_insurance", "medicalInsurance"),
  residence_permit: readAccountingField(options?.accounting, "residencePermit", "residence_permit"),
  other_expenses: readAccountingField(options?.accounting, "otherExpenses", "other_expenses"),
  acommodation: readAccountingField(options?.accounting, "hostelAccommodation", "acommodation", "hostel_accommodation"),
  admission_fee: readAccountingField(options?.accounting, "admissionFee", "admission_fee"),
  complete_one_year: readAccountingField(options?.accounting, "completeOneYear", "complete_one_year"),
});

/** Returns only keys whose values changed between previous and next states. */
export const buildTaskUpdatePayload = (
  previous: TaskUpdateComparableState,
  next: TaskUpdateComparableState,
): ITaskUpdatePayload => {
  const payload: ITaskUpdatePayload = {};

  STRING_KEYS.forEach((key) => {
    const prevValue = normalizeString(previous[key]);
    const nextValue = normalizeString(next[key]);
    if (prevValue === nextValue) return;
    payload[key] = nextValue;
  });

  NUMERIC_KEYS.forEach((key) => {
    const prevValue = previous[key];
    const nextValue = next[key];
    if (prevValue == null && nextValue == null) return;
    if (prevValue === nextValue) return;
    if (nextValue == null) return;
    payload[key] = nextValue;
  });

  return payload;
};

export const buildTaskUpdatePayloadFromTaskRow = (
  previousRow: ITasksTableRow,
  form: TaskCreateFormState,
  options?: {
    status?: string;
    attachedFile?: string | null;
    applicantId?: number;
    assigneeId?: number;
    executorId?: number;
    accounting?: unknown;
  },
): ITaskUpdatePayload =>
  buildTaskUpdatePayload(
    taskRowToUpdateComparableState(previousRow),
    buildTaskUpdateComparableStateFromForm(form, options),
  );

export const mapTaskUpdatePayloadToTableRowPatch = (payload: ITaskUpdatePayload): Partial<ITasksTableRow> => {
  const patch: Partial<ITasksTableRow> = {};

  if (payload.whoose !== undefined) patch.whoose = payload.whoose;
  if (payload.type !== undefined) patch.type = payload.type;
  if (payload.intake !== undefined) patch.intake = payload.intake;
  if (payload.admission_semester !== undefined) patch.admission_semester = payload.admission_semester;
  if (payload.form_of_education !== undefined) patch.form_of_education = payload.form_of_education;
  if (payload.category !== undefined) patch.category = payload.category;
  if (payload.medium_of_instruction !== undefined) patch.medium_of_instruction = payload.medium_of_instruction;
  if (payload.duration !== undefined) patch.duration = payload.duration;
  if (payload.transfer_fee !== undefined) patch.transfer_fee = payload.transfer_fee;
  if (payload.priority !== undefined) patch.priority = payload.priority;
  if (payload.note !== undefined) patch.note = payload.note;
  if (payload.deadline_date !== undefined) patch.deadline_date = payload.deadline_date;
  if (payload.status !== undefined) patch.status = payload.status;
  if (payload.attached_file !== undefined) patch.attached_file = payload.attached_file;
  if (payload.tuition_fee !== undefined) patch.tuition_fee = payload.tuition_fee;
  if (payload.medical_insurance !== undefined) patch.medical_insurance = payload.medical_insurance;
  if (payload.residence_permit !== undefined) patch.residence_permit = payload.residence_permit;
  if (payload.other_expenses !== undefined) patch.other_expenses = payload.other_expenses;
  if (payload.acommodation !== undefined) patch.acommodation = payload.acommodation;
  if (payload.admission_fee !== undefined) patch.admission_fee = payload.admission_fee;
  if (payload.complete_one_year !== undefined) patch.complete_one_year = payload.complete_one_year;
  if (payload.assignee_id !== undefined) patch.assignee_id = payload.assignee_id;
  if (payload.executor_id !== undefined) patch.executor_id = payload.executor_id;

  if (payload.selected_course !== undefined) {
    patch.selected_course = payload.selected_course;
    patch.course = payload.selected_course;
  }

  return patch;
};
